<template>
	<li :class="commentClasses" :data-commentid="comment.id" :id="`comment-${comment.id}`" ref="comment" @click="toggleCollapsed($event, true)">
		<div class="comment__inner">
			<div :class="commentHeadClasses" @click.stop="toggleCollapsed">
				<div class="comment-head__title">
					<img :src="comment.author_avatar" width="40" height="40" class="gravatar comment-avatar">
					
					<strong class="from">
						{{comment.author_display_name}}
					</strong>

					<i
						v-if="isNote"
						class="comment-type icon-internal-note"
						title="Internal Note"
					/>
	
					<small v-if="isAdmin && comment.user_name_behalf_of" class="impersonated-comment-note">
						&nbsp;&mdash; authored by {{comment.user_name_behalf_of}} during <i class="icon icon-users-clients"></i> Impersonation
					</small>
				</div>

				<p :title="fullDatetimeOutput" class="comment-head__date date">{{commentDateOutput}} ({{fromNowDateOutput}})</p>
				
				<div class="comment-head__right">
					<div class="comment-head__actions">
						<ul>
							<li>
								<i v-if="comment.is_email_reply" class="comment-head__icon icon icon-mail" title="This comment is left via email reply." ></i>
							</li>

							<li v-if="issueEmails && issueEmails.length">
								<div class="comment-emails-tracking" @click.stop>
									<i class="comment-head__icon icon icon-user-mail"></i> <span>!</span>
				
									<ul>
										<li>
											<strong>Issue emails:</strong>
										</li>
				
										<li v-for="entry in issueEmails">
											<img v-if="entry.avatar" :src="`//${entry.avatar}`" :alt="entry.name" />
				
											<span v-if="entry.fromNow">{{entry.fromNow}} -</span>
				
											<em>({{entry.type}})</em> {{entry.name}} ({{entry.email}})
										</li>
									</ul>
								</div>
							</li>

							<li v-if="openEmails && openEmails.length">
								<div class="comment-emails-tracking" @click.stop>
									<i class="comment-head__icon icon icon-user-mail"></i>
				
									<ul>
										<li>
											<strong>Seen by:</strong>
										</li>
				
										<li v-for="entry in openEmails">
											<img v-if="entry.avatar" :src="`//${entry.avatar}`" :alt="entry.name" />
				
											<span v-if="entry.fromNow">{{entry.fromNow}} -</span>
				
											{{entry.name}} ({{entry.email}})
										</li>
									</ul>
								</div>
							</li>

							<li v-if="hasAttachment">
								<i  class="comment-head__icon icon icon-paperclip"></i>
							</li>

							<li>
								<a :href="commentStarUrl" :class="starClasses" @click.prevent.stop="toggleStarred">
									<i class=" comment-head__icon icon icon-star"></i>
								</a>
							</li>

							<li v-if="hasUrgency && isManager">
								<i class="comment-head__icon icon icon-alert-triangle" title="High urgency detected" style="color: #ff4444;"></i>
							</li>

							<li>
								<div class="comment-head__dd">
									<a href="#" class="comment-head__icon comment-head__dd-trigger icon icon-more-vertical" @click.prevent.stop="toggleDropdown"></a>
			
									<div class="comment-head__dd-body" v-show="dropDownExpanded">
										<div class="comment-dd">
											<ul>
												<li>
													<a @click.prevent.stop href="#" class="copy-markdown" ref="copyAsMardown">
														Copy as markdown
														<span class="dd-shortcut">{{ shortcutCopyAsMarkdown }}</span>
													</a>
												</li>
								
												<li v-if="isAdmin">
													<span @click.prevent.stop class="disabled-copy-link" v-show="copySelectionAsMarkdownDisabled">
														Copy selection as markdown
														<span class="dd-shortcut">{{ shortcutCopyAsMarkdown }}</span>
													</span>
								
													<a @click.prevent.stop href="#" ref="copySelectionAsMarkdown" v-show="!copySelectionAsMarkdownDisabled">
														Copy selection as markdown
														<span class="dd-shortcut">{{ shortcutCopyAsMarkdown }}</span>
													</a>
												</li>
								
												<li>
													<a @click.prevent.stop href="#" ref="copyPermalink">
														Copy permalink
													</a>
												</li>
								
												<li v-if="comment.is_email_reply">
													<a @click.prevent="downloadEmail">
														Download email
													</a>
												</li>
								
												<li v-if="isAdmin && isOwnComment && isNote">
													<a @click.prevent="removeComment">
														Remove
													</a>
												</li>
											</ul>
										</div>
									</div>
								</div>
							</li>
						</ul>
					</div>
				</div>

				<p v-if="collapsedComment" class="short-desc overflow-truncate" v-html="comment.excerpt"></p>
			</div>
	
			<div v-if="!collapsedComment"  class="comment-body">
				<div class="comment-entry " v-html="commentEntryParsed"></div>
				
				<comment-file-list v-if="hasFiles" :download-batch-link="comment.fileserver_download_batch_link" :files="comment.files" />
			</div>
		</div>
		
		<div class="comment-actions">
			<div class="copy-success">
				<p>Copied! </p>
			</div>
		</div>
	</li>
</template>

<script>
import axios from 'axios';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import Clipboard from 'clipboard';

import { commentDate, fromNow, fullDatetime } from '../filters';
import CommentFileList from '../components/CommentFileList.vue';
import { htmlToMarkdown, selectedTextAsMarkdown } from '../lib/html-to-markdown';

dayjs.extend(utc)
dayjs.extend(timezone)

export default {
	name: 'comment',

	props: {
		comment: {
			required: true,
		},
	},

	components: {
		CommentFileList,
	},

	data () {
		return {
			starred: this.comment.is_starred,

			copied: false,
			dropDownExpanded: false,
			copySelectionAsMarkdownDisabled: false,
			rightNow: (new Date()).getTime()
		};
	},

	computed: {
		collapsedComment() {
			if(typeof this.comment.is_expanded === 'boolean') {
				return !this.comment.is_expanded;
			}

			return true;
		},

		shortcutCopyAsMarkdown() {
			const mainModifier = navigator.platform.toUpperCase().indexOf('MAC') >= 0 ? "Cmd" : "Ctrl";
			return mainModifier + '+Alt+C';
		},

		fromNowDateOutput() {
			return fromNow(this.comment.added_on, this.rightNow );
		},

		commentDateOutput() {
			return commentDate(this.comment.added_on);
		},

		fullDatetimeOutput() {
			return fullDatetime(this.comment.added_on)
		},

		/**
		 * Whether current user is admin.
		 * @return {Boolean}
		 */
		isAdmin() {
			return window.is_admin;
		},

		/**
		 * Current user id.
		 * @return {Number}
		 */
		currentUserId() {
			return window.$$current_user;
		},

		/**
		 * Whether current user is comment's author.
		 * @return {Boolean}
		 */
		isOwnComment() {
			return parseInt(this.comment.user_id) === parseInt(this.currentUserId);
		},

		/**
		 * Get comment type.
		 *
		 * @return {String}
		 */
		type() {
			return this.comment && this.comment.comment_type;
		},

		/**
		 * Whether comment is of type 'note'.
		 *
		 * @return {Boolean}
		 */
		isNote() {
			return this.type === 'note';
		},
		
		/**
		 * Whether comment has files attached to it
		 *
		 * @return {Boolean}
		 */
		hasAttachment() {
			return this.comment.files && this.comment.files.length;
		},

		commentClasses() {
			const classes = [
				'comment',
				`${this.comment.user_type}-comment`,
				{ 'collapsed-comment': this.collapsedComment },
				{ 'copied': this.copied },
			];

			if (this.isNote) {
				classes.push('comment--note');
			}

			if (this.isManager && this.hasUrgency && ! this.allProofQuotesFound) {
				classes.push('comment--urgent');
			}

			return classes;
		},

		commentHeadClasses() {
			return [
				'comment-head',
				{ 'unread': this.comment.is_unread },
			];
		},

		starClasses() {
			return [
				'star-ctrl',
				{ 'has-star': this.starred },
			];
		},

		commentStarUrl() {
			return `/project/${this.comment.project_token}/switch-comment-star-status/${this.comment.id}`;
		},

		hasFiles() {
			return this.comment.files && this.comment.files.length;
		},

		openEmails() {
			return this.getCommentEmailTrackingMeta(['Open']);
		},

		issueEmails() {
			const issueTypes = ['Reject', 'Bounce', 'Complaint'];

			return this.getCommentEmailTrackingMeta(issueTypes);
		},

		commentEntryParsed() {
			let entry = this.comment.entry || this.comment.comment || '';

			if (this.isManager && this.hasUrgency && this.allProofQuotesFound) {
				for (const quote of this.comment.meta.proof_quotes) {
					entry = entry.replaceAll(quote, `<span class="urgency-quote">${quote}</span>`);
				}
			}

			entry = entry.replace(/(\!emergency|\!urgent)/gi, '<strong class="emergency">$1</strong>');

			return entry;
		},

		/**
		 * Whether comment has urgency meta field.
		 * @return {Boolean}
		 */
		hasUrgency() {
			return this.comment.meta && this.comment.meta.urgency;
		},

		hasProofQuotes() {
			const len = this?.comment?.meta?.proof_quotes?.length ?? 0;
			return len > 0;
		},

		allProofQuotesFound() {
			if (! this.hasProofQuotes) {
				return false;
			}

			const entry = this.comment.entry || this.comment.comment || '';

			for (const quote of this.comment.meta.proof_quotes) {
				if (! entry.includes(quote)) {
					return false;
				}
			}

			return true;
		},

		/**
		 * Whether current user is a manager.
		 * @return {Boolean}
		 */
		isManager() {
			return window.is_manager || window.is_admin;
		},
	},

	methods: {
		fromNow,

		toggleCollapsed(_, value) {
			this.$emit('comment:toggle-collapsed', {
				commentId: this.comment.id,
				isCollapsed: value || this.collapsedComment,
			})
		},

		toggleDropdown() {
			this.dropDownExpanded = !this.dropDownExpanded;

			if (this.dropDownExpanded) {
				$(document).one('click', () => this.dropDownExpanded = false);
				this.copySelectionAsMarkdownDisabled = $.trim(selectedTextAsMarkdown()) === '';
			}
		},

		getExtension(filename) {
			const matches = filename.match(/[^\.]+$/);

			if (!matches) {
				return 'unknown';
			}

			return matches[0].toLowerCase().replace(/[^\w]+/, '');
		},

		getExtensionIco(filename) {
			return 'ico-' + this.getExtension(filename);
		},

		toggleStarred() {
			this.starred = !this.starred;

			return axios.get(this.commentStarUrl)
				.then(response => {
					// pass
				})
				.catch(err => {
					console.error(err);
				});
		},
		getPermalink() {
			const anchor = '#comment-' + this.comment.id;
			return window.location.toString().replace(/#.*$/, '') + anchor;
		},

		getFilesListAsMarkdown() {
			if (this.comment.files.length === 0) {
				return '';
			}
			const files = this.comment.files.map(file => '[' + file.original_name + '](' + (file.attachment_permalink || file.fileserver_url || file.download_permalink) + ')' );
			return "\n\n--------\n\n**Files:** \n\n * " + files.join( "\n * ");
		},
		getCommentAsMarkdown() {
			return htmlToMarkdown(this.comment.entry || this.comment.comment) + this.getFilesListAsMarkdown()
		},
		getSelectedTextAsMarkdown() {
			const selection = selectedTextAsMarkdown();
			if (!selection) {
				return false;
			}
			return selection + this.getFilesListAsMarkdown()
		},

		setupClipboard() {
			const copyOptions = [
				{
					elem: this.$refs.copyAsMardown,
					callback: this.getCommentAsMarkdown
				},

				{
					elem: this.$refs.copyPermalink,
					callback: this.getPermalink
				},

				{
					elem: this.$refs.copySelectionAsMarkdown,
					callback: this.getSelectedTextAsMarkdown
				},
			];

			
			for (var i = 0; i < copyOptions.length; i++) {
				const opt = copyOptions[i];
				if (opt.elem) {
					const clipboard = new Clipboard(opt.elem, {
						text: opt.callback
					});
					clipboard.on('success', () => {
						this.flashAfterCopy();
						this.dropDownExpanded = false;
					})
					clipboard.on('error', () => {
						alert("Couldn't copy to your clipboard. Please use your OS native capabilities. ");
					});
				}
			}
		},

		flashAfterCopy() {
			this.copied = true;

			setTimeout(() => {
				this.copied = false;
			}, 100)
		},

		downloadEmail() {
			window.open(`/download-email/${this.comment.email_object}`);
		},

		getCommentEmailTrackingMeta(types) {
			return (this.comment.emails_tracking || [])
				.filter(meta => types.includes(meta.meta_key))
				.filter(meta => meta.user && meta.user.type === 'client')
				.filter((meta, index, array) => array.findIndex(_meta => _meta.user.mail === meta.user.mail) === index)
				.map(meta => {
					return {
						type: meta.meta_key,
						email: meta.user.mail,
						name: meta.user.name,
						avatar: meta.user.avatar,
						fromNow: meta.updated_at ? this.fromNow(meta.updated_at) : '',
					};
				});
		},

		/**
		 * Remove the comment.
		 * @return {Promise}
		 */
		removeComment() {
			const confirm = window.confirm('Are you sure you want to delete thsi comment?');

			if (!confirm) {
				return;
			}

			const commentId = this.comment.id;
			const removeCommentUrl = `${window.location.pathname}/comment/${commentId}/delete`;

			this.$emit('comment:removed', commentId);

			return axios.get(removeCommentUrl);
		}
	},

	mounted() {
		if (this.comment.slideReveal) {
			$(this.$refs.comment).hide().slideDown(200);
		}

		this.setupClipboard()

		// Update the "X minutes ago" live
		setInterval(() => {
			this.rightNow = (new Date()).getTime();
		}, 30 * 1000);
	},
};


</script>
